import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Alibaba, ElevenLabs, Google, Grok, Qwen, Runway, Sora } from '@lobehub/icons';
import { ChevronDown, Info, Zap } from 'lucide-react';
import React from 'react';
import { TOOLTIP_CLASSES } from './ControlIcons';

interface ModelSelectorProps {
  model: string;
  activeDropdown: string | null;
  toggleDropdown: (name: string) => void;
  handleSelect: (type: string, val: string, label: string) => void;
  getMinCost: (modelPrefix: string) => number | null;
  user: any;
  highlighted: boolean;
  autoSwitchEnabled: boolean;
}

export function ModelSelector({
  model,
  activeDropdown,
  toggleDropdown,
  handleSelect,
  getMinCost,
  user,
  highlighted,
  autoSwitchEnabled,
}: ModelSelectorProps) {
  const SoraColor = (Sora as any).Color || Sora;
  const GrokColor = (Grok as any).Color || Grok;
  const RunwayColor = (Runway as any).Color || (Runway as any).color || Runway;
  const QwenColor = (Qwen as any).Color || Qwen;
  const GoogleColor = (Google as any).Color || Google;
  const ElevenLabsColor = (ElevenLabs as any).Color || (ElevenLabs as any).color || ElevenLabs;
  const [infoOpen, setInfoOpen] = React.useState(false);
  const isTouchDevice = React.useRef(false);

  React.useEffect(() => {
    isTouchDevice.current = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }, []);

  const handleInfoPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isTouchDevice.current) {
      setInfoOpen((prev) => !prev);
    } else {
      setInfoOpen(true);
    }
  };

  return (
    <div className="control-group relative">
      <button
        className={cn('custom-select model-select', highlighted && 'pulse-highlight')}
        onClick={() => toggleDropdown('model')}
        data-model={model}
        aria-haspopup="listbox"
        aria-expanded={activeDropdown === 'model'}
      >
        <span className="select-icon">
          {model === 'elevenlabs-sfx' ? (
            <ElevenLabsColor size={20} />
          ) : model === 'z-image' ? (
            <QwenColor size={20} />
          ) : model === 'nano-banana-2' ? (
            <GoogleColor size={20} />
          ) : model === 'runway' ? (
            <RunwayColor size={20} />
          ) : model === 'grok-imagine' ? (
            <GrokColor size={20} />
          ) : (
            <SoraColor size={20} />
          )}
        </span>
        <span className="flex h-full select-text items-center gap-1">
          {model === 'sora-2' ? (
            <>
              Sora<span className="name-extra"> 2</span>
            </>
          ) : model === 'sora-2-pro' ? (
            <>
              <span className="name-extra">Sora 2</span> Pro
            </>
          ) : model === 'elevenlabs-sfx' ? (
            <>
              <span className="name-extra">ElevenLabs</span> SFX
            </>
          ) : model === 'z-image' ? (
            <>Z-Image</>
          ) : model === 'runway' ? (
            <>
              <span className="name-extra">Runway</span> Gen 4
            </>
          ) : model === 'grok-imagine' ? (
            <>
              <span className="name-extra">Grok</span> Imagine
            </>
          ) : (
            <>
              <span className="name-extra">Nano Banana 2</span>
            </>
          )}
        </span>
        <span className="select-arrow">
          <ChevronDown
            className={`h-3 w-3 transition-transform ${activeDropdown === 'model' ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {activeDropdown === 'model' && (
        <div className="select-options active">
          <div
            className={cn(
              'option flex items-center justify-between',
              autoSwitchEnabled && 'selected'
            )}
            style={{ fontWeight: 500 }}
            onClick={() => handleSelect('model', 'auto-switch', 'Auto-Switch')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <Zap size={20} className={cn('text-white', autoSwitchEnabled && 'fill-white')} />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm md:text-[1.02rem]">Auto-Switch</span>
                <div className="relative flex items-center">
                  <button
                    type="button"
                    className="flex items-center justify-center rounded-full p-1 hover:bg-white/10"
                    onPointerDown={handleInfoPointerDown}
                    onMouseEnter={() => !isTouchDevice.current && setInfoOpen(true)}
                    onMouseLeave={() => !isTouchDevice.current && setInfoOpen(false)}
                    onBlur={() => setInfoOpen(false)}
                  >
                    <Info className="h-3 w-3 text-gray-400 md:h-3.5 md:w-3.5" />
                  </button>
                  {infoOpen && (
                    <div
                      className={cn(
                        TOOLTIP_CLASSES,
                        '-translate-x-1/2 fade-in zoom-in-95 absolute bottom-full left-1/2 z-[11000] mb-2 w-[220px] animate-in border-none text-center shadow-2xl duration-200'
                      )}
                    >
                      Automatically bypasses outages and filters. <br />
                      Guaranteed same <Zap className="mb-0.5 inline h-3 w-3 fill-current" /> or
                      fewer.
                      <div className="-mt-[1px] -translate-x-1/2 absolute top-full left-1/2 border-8 border-transparent border-t-[#e2e3e2]" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <span className="ml-auto flex items-center gap-[2px] font-medium text-[8px] text-gray-400 md:text-[10px]">
              Recommended
            </span>
          </div>

          <div className="dropdown-header">Images</div>

          <div
            className={cn(
              'option flex items-center justify-between',
              model === 'z-image' && 'selected'
            )}
            onClick={() => handleSelect('model', 'z-image', 'Z-Image')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <QwenColor size={20} />
              </div>
              Z-Image
            </div>
            {getMinCost('z-image') !== null && (
              <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                <Zap className="h-[10px] w-[10px] fill-current" />
                {getMinCost('z-image')}
              </span>
            )}
          </div>

          <div
            className={cn(
              'option flex items-center justify-between',
              model === 'nano-banana-2' && 'selected'
            )}
            onClick={() => handleSelect('model', 'nano-banana-2', 'Nano Banana 2')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <GoogleColor size={20} />
              </div>
              Nano Banana 2
            </div>
            {getMinCost('nano-banana-2-') !== null && (
              <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                <Zap className="h-[10px] w-[10px] fill-current" />
                {getMinCost('nano-banana-2-')}
              </span>
            )}
          </div>

          <div className="dropdown-header">Videos</div>

          <div
            className={cn(
              'option flex items-center justify-between',
              model === 'grok-imagine' && 'selected'
            )}
            onClick={() => handleSelect('model', 'grok-imagine', 'Grok Imagine')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <GrokColor size={20} />
              </div>
              Grok Imagine
            </div>
            {getMinCost('grok-') !== null && (
              <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                <Zap className="h-[10px] w-[10px] fill-current" />
                {getMinCost('grok-')}
              </span>
            )}
          </div>

          <div
            className={cn(
              'option flex items-center justify-between',
              model === 'runway' && 'selected'
            )}
            onClick={() => handleSelect('model', 'runway', 'Runway Gen 4')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <RunwayColor size={20} />
              </div>
              Runway Gen 4
            </div>
            {getMinCost('runway-') !== null && (
              <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                <Zap className="h-[10px] w-[10px] fill-current" />
                {getMinCost('runway-')}
              </span>
            )}
          </div>

          <div
            className={cn(
              'option flex items-center justify-between',
              model === 'sora-2' && 'selected'
            )}
            onClick={() => handleSelect('model', 'sora-2', 'Sora 2')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <SoraColor size={20} />
              </div>
              Sora 2
            </div>
            {getMinCost('sora-2-') !== null && (
              <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                <Zap className="h-[10px] w-[10px] fill-current" />
                {getMinCost('sora-2-')}
              </span>
            )}
          </div>

          <div
            className={cn(
              'option flex items-center justify-between',
              model === 'sora-2-pro' && 'selected'
            )}
            onClick={() => handleSelect('model', 'sora-2-pro', 'Sora 2 Pro')}
          >
            <div className="flex items-center gap-2">
              <div className="icon-container">
                <SoraColor size={20} />
              </div>
              Sora 2 Pro
            </div>
            {getMinCost('sora-2-pro-') !== null && (
              <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                <Zap className="h-[10px] w-[10px] fill-current" />
                {getMinCost('sora-2-pro-')}
              </span>
            )}
          </div>

          {user?.role === 'admin' && (
            <>
              <div className="dropdown-header">Admin</div>
              <div
                className={cn(
                  'option flex items-center justify-between',
                  model === 'elevenlabs-sfx' && 'selected'
                )}
                onClick={() => handleSelect('model', 'elevenlabs-sfx', 'SFX')}
              >
                <div className="flex items-center gap-2">
                  <div className="icon-container">
                    <ElevenLabsColor size={20} />
                  </div>
                  SFX
                </div>
                {getMinCost('elevenlabs-sfx') !== null && (
                  <span className="ml-auto flex items-center gap-[2px] text-[10px] text-gray-400">
                    <Zap className="h-[10px] w-[10px] fill-current" />
                    {getMinCost('elevenlabs-sfx')}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
